import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FlightPostBookingSelectors } from "@b2bportal/core-flights";
import { useTenantImages } from "@b2bportal/core-themes";
import { navigateTo, purchaseApi, TRIPS_PATH } from "@b2bportal/core-utilities";
import {
  type CipherText,
  type FulfillRequest,
  type FulfillResponse,
  FulfillResponseEnum,
} from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { type ISummaryLineItem, SummaryLineType } from "@overrides/types";
import {
  getOriginalItinerary,
  getPassengers,
} from "../../store/policySelectors";

export const useFlightExchangeRebook = () => {
  const { t } = useI18nContext();

  // TODO: Source these properly
  const travelers = useSelector(getPassengers);
  const contactInfo = {
    phone: travelers?.contact.contact.phoneNumber,
    email: travelers?.contact.contact.emailAddress,
  };

  const [isLoading, setIsLoading] = useState(true);
  const [loadingTitle, setLoadingTitle] = useState("");
  const [quoteToken] = useState<CipherText>();

  const itinerary = useSelector(getOriginalItinerary);
  const updatedItinerary = useSelector(
    FlightPostBookingSelectors.getSelectedFlightItinerary
  );
  const updatedItineraryDetails = useMemo(() => {
    if (updatedItinerary.data == null) return null;

    const {
      bookedItinerary: {
        id: updatedItineraryId,
        passengers: { contact },
        travelItinerary: { locators, slices },
      },
      emailAddress,
    } = updatedItinerary.data;
    const confirmationEmailAddress =
      emailAddress ?? contact.contact.emailAddress;

    return {
      context: {
        itineraryDetails: {
          slices,
          locator: locators?.b2b ?? locators?.agent.unscopedValue,
        },
      },
      handlers: {
        goToMyTrips: () =>
          navigateTo(`${TRIPS_PATH}/flights/${updatedItineraryId}`, {}),
      },
      text: {
        pageBody: t("exchangeable.core.confirmation.header.body", {
          context: "exchangeConfirmed",
          email: confirmationEmailAddress,
        }),
      },
    };
  }, [t, updatedItinerary.data]);

  const itineraryData = useMemo(() => {
    const bookedItinerary = itinerary?.bookedItinerary;
    const sellingPricing = bookedItinerary?.sellingPricing;
    return {
      sellingPricing,
    };
  }, [itinerary]);

  const totalPricing = itineraryData?.sellingPricing?.totalPricing;
  // TODO: Get pricing breakdown
  const priceBreakdown = {
    tripPriceLineItems: [
      {
        baseAmount: {
          label: "Base Amount",
          value: "$458.59",
        },
        taxesAndFees: {
          label: "Taxes",
          value: "$64.59",
        },
      },
    ],
    summaryLineItems: [
      {
        label: "Total",
        // TODO: ensure that this price formatting is correct
        value: totalPricing?.total.fiat.value.toString(),
        type: SummaryLineType.TOTAL,
      } as ISummaryLineItem,
    ],
  };
  // TODO: Get existing payment method details
  const paymentMethod = {
    identifier: "VISA ****5432",
    type: "Credit card",
  };

  //TODO: get real value
  const totalPayableDifference = "$523.18";

  const pollFulfillRebook = useCallback(
    async (fulFillCipherText: CipherText) => {
      try {
        const delayTimes = [1000];
        let pollComplete = false;
        let index = 0;

        while (!pollComplete) {
          const currentDelayTime = delayTimes[index];
          if (index < delayTimes.length) {
            index++;
          }

          await new Promise((resolve) => setTimeout(resolve, currentDelayTime));

          const response: FulfillResponse = (
            await purchaseApi.apiV1PurchaseFulfillPollPut(fulFillCipherText)
          ).data;

          switch (response.FulfillResponse) {
            case FulfillResponseEnum.Failure: {
              pollComplete = true;
              setIsLoading(false);
              // https://hopper-jira.atlassian.net/browse/CXP-3587
              console.error("TODO Replace with agent fulfill request");
              break;
            }
            case FulfillResponseEnum.Pending:
              continue;
            case FulfillResponseEnum.Success: {
              pollComplete = true;
              setIsLoading(false);
              return response;
            }
            case FulfillResponseEnum.PendingInteractivePaymentAuth: {
              // TODO: Should interactive payment auth fail for exchange?
              // if (context.cartFulfill.completeInteractive.results.length > 0) {
              //   continue;
              // }
              pollComplete = true;
              setIsLoading(false);
              return response;
            }
          }
        }
      } catch {
        // https://hopper-jira.atlassian.net/browse/CXP-3587
        console.error("TODO Replace with agent fulfill request");
      }
    },
    []
  );

  const scheduleFulfillRebook = useCallback(async () => {
    setLoadingTitle(t("exchangeable.loading.fulfill"));
    const options = {
      headers: {
        Accept:
          "text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8",
        "Color-Depth": window.screen.colorDepth,
        "Java-Enabled": false, // navigator.javaEnabled() is deprecated. Most modern web browsers no longer support Java applets.
        "JavaScript-Enabled": true,
        "Screen-Width": window.innerWidth,
        "Screen-Height": window.innerHeight,
        "Timezone-Offset": new Date().getTimezoneOffset(),
      },
    };

    if (quoteToken) {
      const fulfillRequest: FulfillRequest = {
        quoteToken,
        payments: [], // TODO: Payment method to charge
      };

      try {
        const result: CipherText = (
          await purchaseApi.apiV1PurchaseFulfillSchedulePost(
            fulfillRequest,
            options
          )
        ).data;

        if (result.value) {
          await pollFulfillRebook(result);
        } else {
          // https://hopper-jira.atlassian.net/browse/CXP-3587
          console.error("TODO Replace with agent fulfill request");
        }
      } catch {
        // https://hopper-jira.atlassian.net/browse/CXP-3587
        console.error("TODO Replace with agent fulfill request");
      }
    } else {
      // https://hopper-jira.atlassian.net/browse/CXP-3587
      console.error("TODO Replace with agent fulfill request");
    }
  }, [pollFulfillRebook, quoteToken, t]);

  const images = useTenantImages();

  return {
    copy: {
      contactInfo: t("reviewContactInformation"),
      phoneNumber: t("phoneNumber"),
      email: t("email"),
      travelerInfo: t("travelerInformation"),
      priceBreakdown: t("priceBreakdown"),
      confirmButton: t("exchangeable.core.rebook.confirmButton"),
      paymentMethod: t("paymentMethod"),
      rebookTitle: t("exchangeable.core.rebook.title"),
      rebookSubtitle: t("exchangeable.core.rebook.subtitle"),
      totalPayableDifference: t(
        "exchangeable.core.rebook.totalPayableDifference"
      ),
      outbound: t("outbound"),
      return: t("return"),
    },
    context: {
      contactInfo: contactInfo,
      travelers: travelers,
      priceBreakdown: priceBreakdown,
      totalPayableDifference: totalPayableDifference,
      paymentMethod: paymentMethod,
      isLoading: isLoading,
      loadingTitle: loadingTitle,
      ...updatedItineraryDetails?.context,
      images,
    },
    handlers: {
      scheduleFulfillRebook,
    },
  };
};
